/**
 * @file MineralPricing.js
 * @version 2.7.4
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-04
 * @modified 2026-09-29
 * @description Mineral Pricing Management - Linked to Inventory
 */

import React, { useState, useEffect, useMemo } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './ContractPricing.css';
import './ResizableColumns.css';
import './MineralPricing.css';
import Input from './Input';
import PaginationControls from './PaginationControls';
import MineralPricingModal from './MineralPricingModal';
import MineralBulkPricingModal from './MineralBulkPricingModal';
import LoadingSpinner from './LoadingSpinner';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import Dropdown from './Dropdown';
import { formatCurrency } from '../utils/currencyFormatter';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import { useSlideOutButton } from './SlideOutButton';
import { useDragSelect } from '../hooks/useDragSelect';
import usePersistedState from '../hooks/usePersistedState';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { matchesAllTerms } from '../utils/searchMatch';
import SearchHelp from './SearchHelp';

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight (utils/activeFilters.js).
 */
const CLEARED_FILTERS = { search: '', limit: '20', activeOnly: true };
const FILTER_LABELS = { activeOnly: 'Archived' };

function MineralPricing() {
  const [minerals, setMinerals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('edit');
  const [selectedMineral, setSelectedMineral] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkPricing, setShowBulkPricing] = useState(false);

  // Slideout buttons — re-measure when bulk bar appears/disappears.
  useSlideOutButton([selectedIds.length]);

  // Drag-to-resize columns (8 cols: checkbox, product, cost, haulage,
  // price, margin, notes, actions).
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('mineralPricing', 9);
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [sorting, setSorting] = usePersistedState('mineralPricing.sorting', { field: '', direction: 'asc' });
  const [pagination, setPagination] = usePersistedState('mineralPricing.pagination', {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [filters, setFilters] = usePersistedState('mineralPricing.filters', {
    search: '',
    limit: '20',
    activeOnly: true
  });

  useEffect(() => {
    loadMinerals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filters.limit to pagination.limit
  useEffect(() => {
    const newLimit = parseInt(filters.limit) || 50;
    setPagination(prev => ({
      ...prev,
      limit: newLimit,
      page: 1,
      totalPages: Math.ceil(prev.total / newLimit)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.limit]);

  const loadMinerals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 10000 // Set high limit for client-side filtering/pagination
      });

      const response = await fetch(`${API_URL}/api/pricing/minerals?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      const allMinerals = data.data || [];
      setMinerals(allMinerals);

      // Reset to page 1 when data changes
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: allMinerals.length,
        totalPages: Math.ceil(allMinerals.length / prev.limit)
      }));
    } catch (error) {
      console.error('Error loading minerals:', error);
      setMinerals([]);
      setPagination(prev => ({ ...prev, page: 1, total: 0, totalPages: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Filter, sort, and paginate minerals
  const filteredAndSortedMinerals = minerals
    .filter(mineral => {
      // Active-Only toggle hides archived rows.
      if (filters.activeOnly && mineral.is_archived) return false;

      if (!filters.search) return true;
      return matchesAllTerms([mineral.item_name, mineral.item_code], filters.search);
    })
    .sort((a, b) => {
      if (!sorting.field) return 0;

      let aVal = a[sorting.field];
      let bVal = b[sorting.field];

      // Handle numeric fields
      if (['cost', 'haulage', 'price', 'margin', 'combined_margin'].includes(sorting.field)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else {
        // Handle string fields (case-insensitive)
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sorting.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sorting.direction === 'asc' ? 1 : -1;
      return 0;
    });

  // Update pagination when filters or sorting changes
  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      page: 1,
      total: filteredAndSortedMinerals.length,
      totalPages: Math.ceil(filteredAndSortedMinerals.length / prev.limit)
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAndSortedMinerals.length, filters.search, filters.activeOnly, sorting.field, sorting.direction]);

  // Paginate the filtered and sorted results
  const paginatedMinerals = filteredAndSortedMinerals.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleAddNew = () => {
    setSelectedMineral(null);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleView = (mineral) => {
    setSelectedMineral(mineral);
    setModalMode('view');
    setShowModal(true);
  };

  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '20' }));
    setSorting({ field: '', direction: 'asc' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any header filter on lights the Clear Filters button.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, { labels: FILTER_LABELS });
  const filtersApplied = headerFilters.count > 0;

  const handleEdit = (mineral) => {
    setSelectedMineral(mineral);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleArchive = async (mineral) => {
    const archiving = !mineral.is_archived;
    try {
      const response = await fetch(`${API_URL}/api/pricing/minerals/${mineral.id}/archive`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_archived: archiving })
      });
      if (response.ok) {
        loadMinerals();
      } else {
        setAlertDialog({ isOpen: true, message: `Failed to ${archiving ? 'archive' : 'unarchive'} mineral pricing`, type: 'error' });
      }
    } catch (error) {
      console.error('Error archiving mineral pricing:', error);
      setAlertDialog({ isOpen: true, message: `Error ${archiving ? 'archiving' : 'unarchiving'} mineral pricing`, type: 'error' });
    }
  };

  const handleBulkArchive = (archiving) => {
    if (selectedIds.length === 0) return;
    setConfirmDialog({
      isOpen: true,
      message: `${archiving ? 'Archive' : 'Unarchive'} ${selectedIds.length} mineral pricing row${selectedIds.length !== 1 ? 's' : ''}?`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          const response = await fetch(`${API_URL}/api/pricing/minerals/bulk-archive`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ ids: selectedIds, is_archived: archiving })
          });
          if (response.ok) {
            const data = await response.json();
            setSelectedIds([]);
            await loadMinerals();
            setAlertDialog({ isOpen: true, message: `${data.updated || 0} mineral pricing row(s) ${archiving ? 'archived' : 'unarchived'}`, type: 'success' });
          } else {
            setAlertDialog({ isOpen: true, message: `Failed to bulk ${archiving ? 'archive' : 'unarchive'}`, type: 'error' });
          }
        } catch (error) {
          console.error('Error bulk archiving:', error);
          setAlertDialog({ isOpen: true, message: 'Bulk archive failed', type: 'error' });
        }
      }
    });
  };

  // The modal reports the outcome of its save (which may include a Xero push of
  // the cost) so the result — success, or saved-but-Xero-push-failed — is always
  // surfaced rather than the modal just closing silently.
  const handleSave = async (result) => {
    setShowModal(false);
    loadMinerals();
    if (result?.message) {
      setAlertDialog({ isOpen: true, message: result.message, type: result.type || 'success' });
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedMinerals.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(paginatedMinerals.map(m => m.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedIds(prev => {
        if (checked) return prev.includes(id) ? prev : [...prev, id];
        return prev.filter(i => i !== id);
      });
    }
  });

  // Are all currently visible rows archived? Drives bulk action label.
  const allSelectedArchived = selectedIds.length > 0 && minerals
    .filter(m => selectedIds.includes(m.id))
    .every(m => m.is_archived);

  // The context-menu hook works in Sets; this page keeps selection as an array.
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const ctxMenu = useTableContextMenu({
    rows: paginatedMinerals,
    selectedIds: selectedIdSet,
    getRowId: (m) => m.id
  });

  const allPageSelected = paginatedMinerals.length > 0 && selectedIds.length === paginatedMinerals.length;

  // Shared by the row's kebab menu and its right-click context menu.
  const buildRowActions = (mineral) => [
    { key: 'edit', icon: 'edit', label: 'Edit', title: 'Edit', onClick: () => handleEdit(mineral) },
    { key: 'archive', icon: mineral.is_archived ? 'unarchive' : 'archive', label: mineral.is_archived ? 'Unarchive' : 'Archive', title: mineral.is_archived ? 'Unarchive' : 'Archive', onClick: () => handleArchive(mineral) },
  ];

  return (
    <div className="contract-pricing mineral-pricing">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Manage the mineral price list, linked to inventory
          </span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Prices based on inventory cost + haulage + margin. For per-customer mineral contract prices, use Inventory &rarr; Contracts &rarr; Minerals</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={handleClearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">filter_alt_off</span>
                </div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={handleAddNew}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">add</span>
                </div>
                <span className="btn-label">Add New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters" style={{ paddingBottom: "var(--spacing-lg)" }}>
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['product', 'item code']} />
            </label>
            <Input
              type="text"
              placeholder="Product name or code..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="20"
            />
          </div>
          <div className="filter-group">
            <label>Active Only</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="mineral-active-only"
                  type="checkbox"
                  checked={!!filters.activeOnly}
                  onChange={(e) => handleFilterChange('activeOnly', e.target.checked)}
                />
                <label htmlFor="mineral-active-only"></label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="table-toolbar">
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          <ResetButton />
        </div>
        {pagination.totalPages > 0 && (
          <PaginationControls
            pagination={pagination}
            onPageChange={changePage}
          />
        )}
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <div className="bulk-actions-info">
            <label className="tick-check-label" style={{ gap: '0' }}>
              <input type="checkbox" className="tick-check-input" id="bulk-mineral-indicator" checked readOnly />
              <label htmlFor="bulk-mineral-indicator" className="tick-check">
                <svg width="16px" height="16px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                  <polyline points="1 9 7 14 15 4"></polyline>
                </svg>
              </label>
            </label>
            <span>{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <div>
              <button className="btn-slideout" onClick={() => setShowBulkPricing(true)}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">edit_note</span>
                </div>
                <span className="btn-label">Edit Prices</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => handleBulkArchive(!allSelectedArchived)}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">{allSelectedArchived ? 'unarchive' : 'archive'}</span>
                </div>
                <span className="btn-label">{allSelectedArchived ? 'Unarchive' : 'Archive'}</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-slideout btn-danger" onClick={() => setSelectedIds([])}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Clear</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="table-container">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <table className="data-table resizable-table" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
            <ColGroup />
            <thead>
              <tr>
                <th className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="select-all-minerals"
                      checked={paginatedMinerals.length > 0 && selectedIds.length === paginatedMinerals.length}
                      onChange={toggleSelectAll}
                    />
                    <label htmlFor="select-all-minerals" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                    </label>
                  </label>
                  <ResizeHandle colIndex={0} />
                </th>
                <th className="sortable" onClick={() => handleSort('item_name')}>
                  Product {sorting.field === 'item_name' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={1} />
                </th>
                <th style={{ textAlign: "right" }} className="sortable" onClick={() => handleSort('cost')}>
                  Cost {sorting.field === 'cost' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={2} />
                </th>
                <th style={{ textAlign: "right" }} className="sortable" onClick={() => handleSort('haulage')}>
                  Haulage {sorting.field === 'haulage' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={3} />
                </th>
                <th style={{ textAlign: "right" }} className="sortable" onClick={() => handleSort('margin')}>
                  Margin {sorting.field === 'margin' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={4} />
                </th>
                <th style={{ textAlign: "right" }} className="sortable" onClick={() => handleSort('combined_margin')}>
                  Combined {sorting.field === 'combined_margin' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={5} />
                </th>
                <th style={{ textAlign: "right" }} className="sortable" onClick={() => handleSort('price')}>
                  Sale Price {sorting.field === 'price' && (sorting.direction === 'asc' ? '▲' : '▼')}
                  <ResizeHandle colIndex={6} />
                </th>
                <th>Notes<ResizeHandle colIndex={7} /></th>
                <th className="actions-col"><ResizeHandle colIndex={8} /></th>
              </tr>
            </thead>
            <tbody>
              {paginatedMinerals.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    No minerals found
                  </td>
                </tr>
              ) : (
                paginatedMinerals.map((mineral) => (
                  <tr
                    key={mineral.id}
                    className={mineral.is_archived ? 'archived' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleView(mineral)}
                    onContextMenu={ctxMenu.onRowContextMenu(mineral)}
                  >
                    <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                      <label
                        className="tick-check-label"
                        style={{ justifyContent: 'center', gap: '0' }}
                        onMouseDown={dragSelect.onMouseDown(mineral.id, selectedIds.includes(mineral.id))}
                        onMouseEnter={dragSelect.onMouseEnter(mineral.id)}
                        onClick={(e) => e.preventDefault()}
                      >
                        <input
                          type="checkbox"
                          className="tick-check-input"
                          id={`select-mineral-${mineral.id}`}
                          checked={selectedIds.includes(mineral.id)}
                          onChange={() => toggleSelect(mineral.id)}
                        />
                        <label htmlFor={`select-mineral-${mineral.id}`} className="tick-check">
                          <svg width="16px" height="16px" viewBox="0 0 18 18"><path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path><polyline points="1 9 7 14 15 4"></polyline></svg>
                        </label>
                      </label>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
                        <span className="material-symbols-rounded" title={mineral.is_priced ? 'Priced' : 'Not priced'}
                          style={{ fontSize: '16px', color: mineral.is_priced ? 'var(--color-success)' : 'var(--color-error)', flexShrink: 0 }}>
                          sell
                        </span>
                        {mineral.item_code && (
                          <div className="product-code">{mineral.item_code}</div>
                        )}
                        <div>{mineral.item_name}</div>
                        {mineral.is_archived && <span className="archived-badge">ARCHIVED</span>}
                      </div>
                    </td>
                    <td style={{ textAlign: "right" }}>{formatCurrency(mineral.cost)}</td>
                    <td style={{ textAlign: "right" }}>{formatCurrency(mineral.haulage)}</td>
                    <td style={{ textAlign: "right" }}>{mineral.margin != null ? formatCurrency(mineral.margin) : '—'}</td>
                    <td style={{ textAlign: "right" }}>{mineral.combined_margin != null ? formatCurrency(mineral.combined_margin) : '—'}</td>
                    <td style={{ textAlign: "right" }}>
                      <div className="price">{mineral.price != null ? formatCurrency(mineral.price) : 'POA'}</div>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>{mineral.notes || ''}</td>
                    <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                      <RowActionsMenu actions={buildRowActions(mineral)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <TableContextMenu
        ctx={ctxMenu}
        rowActions={buildRowActions}
        bulkActions={(ids) => [
          {
            key: 'bulk-prices',
            icon: 'edit_note',
            label: `Edit prices on ${ids.size} items`,
            onClick: () => setShowBulkPricing(true)
          },
          {
            key: 'bulk-archive',
            icon: allSelectedArchived ? 'unarchive' : 'archive',
            label: `${allSelectedArchived ? 'Unarchive' : 'Archive'} ${ids.size} items`,
            onClick: () => handleBulkArchive(!allSelectedArchived)
          }
        ]}
        actions={[
          { key: 'reload', icon: 'refresh', label: 'Reload minerals', onClick: loadMinerals },
          { key: 'clear-filters', icon: 'filter_alt_off', label: 'Clear filters', onClick: handleClearFilters },
          {
            key: 'select-all',
            icon: allPageSelected ? 'deselect' : 'select_all',
            label: allPageSelected ? 'Clear selection' : 'Select all on this page',
            disabled: paginatedMinerals.length === 0,
            onClick: toggleSelectAll
          },
          {
            key: 'active-only',
            icon: 'inventory_2',
            label: 'Active only',
            active: !!filters.activeOnly,
            onClick: () => handleFilterChange('activeOnly', !filters.activeOnly)
          }
        ]}
      />

      {showModal && (
        <MineralPricingModal
          mineral={selectedMineral}
          mode={modalMode}
          onClose={() => { setShowModal(false); loadMinerals(); }}
          onSave={handleSave}
        />
      )}

      {showBulkPricing && (
        <MineralBulkPricingModal
          minerals={minerals.filter(m => selectedIds.includes(m.id))}
          onClose={() => setShowBulkPricing(false)}
          onSave={() => {
            setShowBulkPricing(false);
            setSelectedIds([]);
            loadMinerals();
          }}
        />
      )}

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />
    </div>
  );
}

export default MineralPricing;
